import React, { useState } from 'react';

import { faFloppyDisk, faPlus, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { Input } from '@buildoutinc/blueprint-react/ui/Input';
import { Table } from '@buildoutinc/blueprint-react/ui/Table';

import { deleteRequest, patch, post } from 'helpers/api';
import { requestErrors } from 'helpers/api_errors';
import { Button } from 'components/ui/button';
import { Errors } from 'components/ui/errors';

import type { PrincipalEmployer } from './types';

interface Props {
  employers: PrincipalEmployer[];
  employersPath: string;
  fiscalYear: number | null;
  // Given an update rather than a list, so saves that finish out of order each apply to the latest rows.
  onChange: (update: (employers: PrincipalEmployer[]) => PrincipalEmployer[]) => void;
}

interface Draft {
  rank: string;
  name: string;
  employees: string;
  pctTotalEmployment: string;
}

function draftFor(employer: PrincipalEmployer | null): Draft {
  return {
    rank: employer?.rank?.toString() ?? '',
    name: employer?.name ?? '',
    employees: employer?.employees?.toString() ?? '',
    pctTotalEmployment: employer?.pctTotalEmployment?.toString() ?? '',
  };
}

function bodyFor(draft: Draft, year: number) {
  return {
    principalEmployer: {
      year,
      rank: draft.rank || null,
      name: draft.name,
      employees: draft.employees || null,
      pctTotalEmployment: draft.pctTotalEmployment || null,
    },
  };
}

// The schedule's rows, one table per year with the report's own year first. Each row saves on its own.
export default function EmployersTable({ employers, employersPath, fiscalYear, onChange }: Props) {
  const years = [...new Set(employers.map(employer => employer.year))].sort((a, b) => b - a);

  function replace(saved: PrincipalEmployer): void {
    onChange(current => current.map(employer => (employer.id === saved.id ? saved : employer)));
  }

  function remove(id: number): void {
    onChange(current => current.filter(employer => employer.id !== id));
  }

  function add(saved: PrincipalEmployer): void {
    onChange(current => [...current, saved]);
  }

  return (
    <div className="d-flex flex-column gap-4">
      {years.map(year => (
        <div key={year}>
          <h4 className="mb-2">
            {year}
            {year !== fiscalYear && (
              <span className="text-muted small ms-2">for reference, not shown in documents</span>
            )}
          </h4>
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.Head className="w-auto">Rank</Table.Head>
                <Table.Head>Employer</Table.Head>
                <Table.Head>Employees</Table.Head>
                <Table.Head>% of employment</Table.Head>
                <Table.Head>Last edited</Table.Head>
                <Table.Head />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {employers
                .filter(employer => employer.year === year)
                .sort((a, b) => (a.rank ?? Infinity) - (b.rank ?? Infinity))
                .map(employer => (
                  <EmployerRow
                    employer={employer}
                    key={employer.id}
                    path={`${employersPath}/${employer.id}`}
                    onRemoved={remove}
                    onSaved={replace}
                  />
                ))}
            </Table.Body>
          </Table>
        </div>
      ))}

      {fiscalYear && <NewEmployerRow employersPath={employersPath} year={fiscalYear} onAdded={add} />}
    </div>
  );
}

interface EmployerRowProps {
  employer: PrincipalEmployer;
  path: string;
  onSaved: (employer: PrincipalEmployer) => void;
  onRemoved: (id: number) => void;
}

function EmployerRow({ employer, path, onSaved, onRemoved }: EmployerRowProps) {
  const [draft, setDraft] = useState<Draft>(() => draftFor(employer));
  const [errors, setErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState<boolean>(false);
  const dirty = JSON.stringify(draft) !== JSON.stringify(draftFor(employer));

  async function save(): Promise<void> {
    setSaving(true);
    try {
      const saved: PrincipalEmployer = await patch(path, bodyFor(draft, employer.year));
      setErrors([]);
      onSaved(saved);
      setDraft(draftFor(saved));
    } catch (error) {
      setErrors(requestErrors(error));
    } finally {
      setSaving(false);
    }
  }

  async function destroy(): Promise<void> {
    if (!window.confirm(`Remove ${employer.name}?`)) return;

    try {
      await deleteRequest(path);
      onRemoved(employer.id);
    } catch (error) {
      setErrors(requestErrors(error));
    }
  }

  return (
    <Table.Row>
      <DraftCells disabled={saving} draft={draft} onChange={setDraft} />
      <Table.Cell className="small text-muted">
        {employer.editedBy && `${employer.editedBy}, ${new Date(employer.editedAt ?? '').toLocaleDateString()}`}
        <Errors errors={errors} title={false} />
      </Table.Cell>
      <Table.Cell className="text-nowrap">
        <Button disabled={!dirty || saving} icon={faFloppyDisk} label="Save" size="sm" onClick={save} />
        <Button icon={faTrash} size="icon-sm" tooltip="Remove" variant="ghost" onClick={destroy} />
      </Table.Cell>
    </Table.Row>
  );
}

interface NewEmployerRowProps {
  employersPath: string;
  year: number;
  onAdded: (employer: PrincipalEmployer) => void;
}

function NewEmployerRow({ employersPath, year, onAdded }: NewEmployerRowProps) {
  const [draft, setDraft] = useState<Draft>(() => draftFor(null));
  const [errors, setErrors] = useState<string[]>([]);
  const [adding, setAdding] = useState<boolean>(false);

  async function add(): Promise<void> {
    setAdding(true);
    try {
      const saved: PrincipalEmployer = await post(employersPath, bodyFor(draft, year));
      onAdded(saved);
      setDraft(draftFor(null));
      setErrors([]);
    } catch (error) {
      setErrors(requestErrors(error));
    } finally {
      setAdding(false);
    }
  }

  return (
    <div>
      <h5 className="mb-2">Add a {year} employer extraction missed</h5>
      <Errors errors={errors} />
      <Table>
        <Table.Body>
          <Table.Row>
            <DraftCells disabled={adding} draft={draft} onChange={setDraft} />
            <Table.Cell>
              <Button disabled={adding || !draft.name} icon={faPlus} label="Add" size="sm" onClick={add} />
            </Table.Cell>
          </Table.Row>
        </Table.Body>
      </Table>
    </div>
  );
}

interface DraftCellsProps {
  // While a save is in flight, so an edit made meanwhile isn't replaced by the saved row.
  disabled: boolean;
  draft: Draft;
  onChange: (draft: Draft) => void;
}

function DraftCells({ disabled, draft, onChange }: DraftCellsProps) {
  function field(key: keyof Draft, type: 'text' | 'number', ariaLabel: string) {
    return (
      <Table.Cell>
        <Input
          aria-label={ariaLabel}
          disabled={disabled}
          step={key === 'pctTotalEmployment' ? 'any' : undefined}
          type={type}
          value={draft[key]}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange({ ...draft, [key]: event.target.value })}
        />
      </Table.Cell>
    );
  }

  return (
    <>
      {field('rank', 'number', 'Rank')}
      {field('name', 'text', 'Employer')}
      {field('employees', 'number', 'Employees')}
      {field('pctTotalEmployment', 'number', '% of employment')}
    </>
  );
}
